import debounce from "lodash.debounce";
import React from "react";

const useInputAsyncValidation = ({
  defaultValue,
  validateOnBlur,
  validateOnFocus,
  asyncValidation,
}: {
  defaultValue: string;
  validateOnBlur?: boolean;
  validateOnFocus?: boolean;
  asyncValidation?: (value: string) => Promise<string | undefined>;
}) => {
  const [value, setValue] = React.useState(defaultValue);
  const [error, setError] = React.useState<string | undefined>();
  const [isValidating, setIsValidating] = React.useState(false);
  const [hasPendingValidation, setHasPendingValidation] = React.useState(false);
  const valueRef = React.useRef<string>(value);
  const latestValidationIdRef = React.useRef(0);

  const debounceAsyncValidation = React.useMemo(
    () =>
      asyncValidation &&
      debounce(
        (newValue: string) => {
          const validationId = latestValidationIdRef.current + 1;
          latestValidationIdRef.current = validationId;
          setHasPendingValidation(false);
          setIsValidating(true);

          void asyncValidation(newValue)
            .then(error => {
              // can have changed during async validation
              if (valueRef.current === newValue) {
                setError(error);
              }
            })
            .finally(() => {
              if (latestValidationIdRef.current === validationId) {
                setIsValidating(false);
              }
            });
        },
        1_000,
        { trailing: true },
      ),
    [asyncValidation],
  );

  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = React.useCallback(
    e => {
      const newValue = e.currentTarget.value ?? "";
      setValue(newValue);
      valueRef.current = newValue;
      if (debounceAsyncValidation) {
        setHasPendingValidation(true);
        debounceAsyncValidation(newValue);
      }
    },
    [debounceAsyncValidation],
  );

  const handleValidation = React.useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement, Element>) => {
      const newValue = event.target.value;
      valueRef.current = newValue;
      if (debounceAsyncValidation) {
        setHasPendingValidation(true);
        debounceAsyncValidation(newValue);
      }
    },
    [debounceAsyncValidation],
  );

  const handleBlur: React.FocusEventHandler<HTMLTextAreaElement> = React.useCallback(
    event => {
      if (validateOnBlur) {
        handleValidation(event);
      }
    },
    [handleValidation, validateOnBlur],
  );

  const handleFocus: React.FocusEventHandler<HTMLTextAreaElement> = React.useCallback(
    event => {
      if (validateOnFocus) {
        handleValidation(event);
      }
    },
    [handleValidation, validateOnFocus],
  );

  React.useEffect(() => {
    if (defaultValue === valueRef.current) return;

    setValue(defaultValue); // remet la valeur
    valueRef.current = defaultValue;
    setError(undefined);
    setIsValidating(false);
    setHasPendingValidation(false);
  }, [defaultValue, debounceAsyncValidation]);

  React.useEffect(
    () => () => {
      debounceAsyncValidation?.cancel();
    },
    [debounceAsyncValidation],
  );

  const resetError = React.useCallback(() => {
    setError(undefined);
  }, []);

  return {
    error,
    hasPendingValidation,
    isValidating,
    handleChange,
    handleBlur,
    handleFocus,
    resetError,
  };
};

export default useInputAsyncValidation;
